import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  ViewChild,
} from '@angular/core';
import { Transition } from '@uirouter/core';
import { BehaviorSubject } from 'rxjs';
import { type FolderItem } from 'src/app/api/services/folder';
import { type Schedule } from 'src/app/api/services/schedule';
import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import {
  ListItem,
  ListSearchStateService,
  PageWithListComponent,
  PaginatedScrollingListService,
  ScrollingListStateService,
  type ListOperations,
} from 'src/app/components/scrolling-list';
import { CompanyIdModalComponent } from 'src/app/components/scrolling-list/company-id-modal/company-id-modal.component';
import { UsernamePipe } from 'src/app/shared/pipes/username.pipe';
import { FolderService } from 'src/app/shared/services/folder.service';
import { SCHEDULE_FOLDER_OPERATION_SERVICE } from '../../providers/schedule-folder-operation.provider';
import { ScheduleService } from '../../services/schedule.service';
import { LiveAnnouncerService } from 'src/app/accessibility';

@Component({
  selector: 'schedule-list',
  templateUrl: './schedule-list.component.html',
  styleUrls: ['./schedule-list.component.scss'],
  standalone: false,
  providers: [scheduleListProviders()],
})
export class ScheduleListComponent
  extends PageWithListComponent<Schedule>
  implements AfterViewInit
{
  private destroyRef = inject(DestroyRef);
  protected schedules$ = new BehaviorSubject<any[]>([]);
  protected folders$ = new BehaviorSubject<any[]>([]);

  @ViewChild('schedulesListContainer') schedulesListContainer: ElementRef;

  folderId: string | null | undefined;
  folders: FolderItem[] = [];

  tableFields: {
    label: string;
    name: string;
    reverse: boolean;
    renderIf: boolean;
  }[];

  listOperations = {
    name: 'Schedule',
    operations: [
      {
        name: 'Copy to Sub-company',
        beforeBatchAction: (selected) => {
          const plural = selected.length > 1 ? 's' : '';
          return this.modalService.showDialog(CompanyIdModalComponent, {
            title: 'Copy Selected Schedule' + plural,
            description:
              'A copy of ' +
              (plural ? 'each of ' : '') +
              'the selected schedule' +
              plural +
              ' and any associated presentations will be added to the Company ID specified below.',
          });
        },
        actionCall: (schedule?, companyId?) => {
          if (schedule && (schedule.item as Schedule).companyId === companyId) {
            this.listOperations.operations[0].reloadList = true;
          }
          return this.scheduleApiService.copy(schedule.item.id, companyId);
        },
        reloadList: false,
        hidden: (selected) => {
          return this.hasFolderSelected(selected);
        },
        requireRole: 'cp',
        mainOperation: false,
      },
      {
        name: 'Export',
        beforeBatchAction: () => {
          return this.modalService.confirm(
            'Export schedules?',
            'An export file will be prepared and emailed to you at <b>' +
              this.userStateService.getUserEmail() +
              '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.',
            'Export',
            'Cancel',
          );
        },
        actionCall: this.scheduleApiService.export.bind(
          this.scheduleApiService,
        ),
        groupBy: true,
        requireRole: 'cp',
        mainOperation: true,
      },
      {
        name: 'Rename',
        beforeBatchAction: (selected) =>
          this.scheduleService.actionRename(selected.map((item) => item.item)),
        showIn: ['single'],
        icon: 'pencil-line',
        reloadList: true,
      },
      {
        name: 'Move',
        beforeBatchAction: (selected) =>
          this.scheduleService.beforeActionMoveFolder(
            selected.map((item) => item.item),
            this.folderId,
          ),
        actionCall: (selected, folderId) =>
          this.scheduleService.actionMoveFolder(selected.item, folderId),
        showIn: ['single', 'bulk'],
        icon: 'move-to-folder',
        mainOperation: true,
        reloadList: true,
      },
      {
        name: 'Delete',
        actionCall: async (listItem) => {
          await this.scheduleFolderOperationService.executeSingleOperation(
            (schedule) => this.scheduleService.deleteScheduleByObject(schedule),
            listItem.item,
          );
          if (listItem && listItem.isFolder) {
            await this.folderService.deleteFolder(listItem.item.id);
          }
          return Promise.resolve();
        },
        requireRole: 'cp',
        mainOperation: true,
        showIn: ['single', 'bulk'],
        icon: 'trash',
        isDelete: true,
      },
    ],
  } as ListOperations<Schedule>;

  filterConfig = {
    placeholder: 'Search Schedules',
    borderless: true,
  };

  assignedToUsers: string[] = [];
  scheduleFolderOperationService = inject(SCHEDULE_FOLDER_OPERATION_SERVICE);

  constructor(
    private scheduleApiService: ScheduleApiService,
    public scheduleService: ScheduleService,
    private modalService: ModalService,
    protected userStateService: UserStateService,
    public companyStateService: CompanyStateService,
    private usernamePipe: UsernamePipe,
    protected folderService: FolderService,
    private liveAnnouncerService: LiveAnnouncerService,
    private transition: Transition,
    private ngZone: NgZone,
  ) {
    super();

    this.tableFields = [
      { label: 'Name', name: 'name', reverse: false, renderIf: true },
      {
        label: 'Override',
        name: 'scheduleType',
        reverse: false,
        renderIf: true,
      },
      { label: 'Timeline', name: 'timeline', reverse: false, renderIf: true },
      {
        label: 'Distribution',
        name: 'distributionSummary',
        reverse: true,
        renderIf: true,
      },
      {
        label: 'Last Modified',
        name: 'changeDate',
        reverse: false,
        renderIf: true,
      },
      {
        label: 'Assigned To',
        name: 'assignedUsersValue',
        reverse: false,
        renderIf:
          this.companyStateService.isK12Customer() ||
          this.companyStateService.isHigherEdCustomer(),
      },
    ];

    this.folderId = this.transition.params().folderId;
    if (this.folderId) {
      this.searchState.setFolderId(this.folderId);
    }
    this.listService.load();
    this.updateMoveAfterAction();
    this.listService.setOperations(this.listOperations);
  }

  ngAfterViewInit(): void {
    const { sortBy, reverse } = this.searchState.getSearchObject();
    const field = this.tableFields.find((f) => f.name === sortBy);
    if (field) {
      this.announceSort(field.label, reverse ? 'descending' : 'ascending');
    }

    this.initDraggableService(
      '.schedules-list-container, .folder-path',
      '.schedules-list-container',
    );
  }

  popoverText(assignedUsers: string[]) {
    if (assignedUsers.length > 1) {
      this.assignedToUsers = assignedUsers
        .slice(1)
        .map((user) => (user ? this.usernamePipe.transform(user) : ''));
    } else {
      this.assignedToUsers = [];
    }

    setTimeout(() => {
      this.ngZone.run(() => {});
    });
  }

  getDestroyRef(): DestroyRef {
    return this.destroyRef;
  }

  onHeaderResize(event: { width: number; height: number }) {
    if (this.schedulesListContainer) {
      this.schedulesListContainer.nativeElement.style.setProperty(
        '--schedules-list-header-height',
        event.height + 'px',
      );

      this.checkViewportSize();
    }
  }

  protected doSearch = () => {
    this.searchState.doSearch();
  };

  hasFolderSelected(selected: ListItem<Schedule>[]) {
    if (!selected) return false;
    return selected.some((item) => item.isFolder && item.selected);
  }

  onFolderClicked(folderId: string) {
    this.transition.router.stateService.go('apps.schedules.list', { folderId });
  }

  sort(field: { name: string; label: string; reverse: boolean }) {
    // Get current sort state before changing it
    const currentSort = this.searchState.getSearchObject();
    const wasAlreadySorted = currentSort.sortBy === field.name;
    const willBeReversed = wasAlreadySorted
      ? !currentSort.reverse
      : field.reverse;

    // Perform the sort
    this.searchState.sortBy(field.name, willBeReversed);

    // Announce the sort change
    this.announceSort(field.label, willBeReversed ? 'descending' : 'ascending');
  }

  announceSort(fieldLabel: string, direction: 'ascending' | 'descending') {
    this.liveAnnouncerService.announce(
      `Table sorted by ${fieldLabel}, ${direction} order`,
    );
  }
}

function scheduleListProviders() {
  return [
    { provide: 'WithListService', useExisting: ScheduleApiService },
    { provide: 'ListKind', useValue: 'Schedule' },
    { provide: ListSearchStateService },
    { provide: ScrollingListStateService },
    { provide: PaginatedScrollingListService },
  ];
}
